import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useViewParam } from "@/lib/useViewParam";
import { Routes, Route, Navigate, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import LhcHeader from "@/components/LhcHeader";
import { useT } from "../i18n";
import AccountPage from "@/store/AccountPage";
import RealtimeProvider from "@/realtime/RealtimeProvider";
import OrdersWorkspace from "@/store/orders/OrdersWorkspace";
import "@/store/StoreApp.css";
import "@/store/clients/clients.css";
import { Icon, Dropdown, SearchInput, ViewToggle, ActionMenu, EmptyState } from "@/store/clients/kit";
import {
  DashboardStatCard, ClockDial, RevenueChart, TopClientsCard, ClientCard, ClientToolbar,
  ClientHeader, ClientTabs, ProjectCard, ProjectSummaryPanel,
} from "@/store/clients/components";
import { Sk, SkRows, SkChart, Reveal } from "@/components/Skeleton";

import { uiConfirm, uiPrompt } from "../lib/dialogs";
import { NEW_PROJECT_PLACEHOLDER } from "../lib/projectNames";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => "€" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money0 = (n) => "€" + Math.round(Number(n || 0)).toLocaleString();

/* City → timezone (mirrors the prototype's home dashboard) */
const CITY_TZ = {
  Tirana: "Europe/Tirane", "Durrës": "Europe/Tirane", "Shkodër": "Europe/Tirane",
  Milan: "Europe/Rome", Rome: "Europe/Rome", Florence: "Europe/Rome",
  Zurich: "Europe/Zurich", Geneva: "Europe/Zurich", Basel: "Europe/Zurich",
  Paris: "Europe/Paris", Lyon: "Europe/Paris",
  Berlin: "Europe/Berlin", Munich: "Europe/Berlin", Hamburg: "Europe/Berlin",
  Vienna: "Europe/Vienna", London: "Europe/London", "New York": "America/New_York",
};
const CITY_COUNTRY = {
  Tirana: "Albania", "Durrës": "Albania", "Shkodër": "Albania",
  Milan: "Italy", Rome: "Italy", Florence: "Italy",
  Zurich: "Switzerland", Geneva: "Switzerland", Basel: "Switzerland",
  Paris: "France", Lyon: "France", Berlin: "Germany", Munich: "Germany",
  Hamburg: "Germany", Vienna: "Austria", London: "UK", "New York": "USA",
};
const HERO_GRADIENT = "linear-gradient(135deg,#2D7FF9,#4facfe)";
const heroGradientFor = () => HERO_GRADIENT;
const tzTime = (tz, d) => { try { return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(d); } catch { return "--:--"; } };

const HOME_SORTS = [
  { key: "recent", label: "Recently added" },
  { key: "name_az", label: "Name (A–Z)" },
  { key: "name_za", label: "Name (Z–A)" },
  { key: "projects", label: "Most projects" },
  { key: "revenue", label: "Highest revenue" },
];
const HOME_FILTERS = [
  { key: "all", label: "All clients" },
  { key: "open", label: "Has open quotes" },
  { key: "orders", label: "Has orders" },
  { key: "projects", label: "Has projects" },
  { key: "leads", label: "Leads (no projects)" },
];
const REV_RANGES = [
  { key: "3m", label: "Last 3 months" },
  { key: "6m", label: "Last 6 months" },
  { key: "12m", label: "Last 12 months" },
];

/* Real-data growth trend: recent-half vs older-half of the monthly creation series.
   Returns null only when there is genuinely no basis (returns up/down + %). */
function trendOf(items, dateKey = "created_at") {
  return seriesTrend(monthlySeries(items, 6, dateKey).map((v) => ({ value: v })));
}
/* Monthly count series for sparklines (oldest → newest). */
function monthlySeries(items, n = 8, dateKey = "created_at") {
  const now = new Date();
  const buckets = Array(n).fill(0);
  for (const it of items) {
    const t = new Date(it[dateKey] || it.createdAt || 0);
    if (isNaN(t)) continue;
    const mi = (now.getFullYear() - t.getFullYear()) * 12 + (now.getMonth() - t.getMonth());
    const idx = n - 1 - mi;
    if (idx >= 0 && idx < n) buckets[idx]++;
  }
  return buckets;
}
/* Monthly money series for area charts → [{label, value}]. */
function monthlyMoney(items, n = 6, totalKey = "total", dateKey = "created_at") {
  const now = new Date();
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en", { month: "short" }), value: 0 });
  }
  const idx = {}; out.forEach((m, i) => { idx[m.key] = i; });
  for (const it of items) {
    const t = new Date(it[dateKey] || it.createdAt || now);
    const k = `${t.getFullYear()}-${t.getMonth()}`;
    if (k in idx) out[idx[k]].value += Number(it[totalKey] || 0);
  }
  return out;
}
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

/* ───────────────────────── Clients home dashboard ───────────────────────── */
function ClientsPage() {
  const t = useT();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  // three-state loading (2026-07-18): no 0/€0/empty renders before the data
  // exists — skeletons until the initial fetches settle, then fade the real
  // content in; true zeros only when the API confirms empty.
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [sortKey, setSortKey] = useState("recent");
  const [filterKey, setFilterKey] = useState("all");
  const [cardMenu, setCardMenu] = useState(null); // client id whose ⋮ menu is open
  const [showCreate, setShowCreate] = useState(false);
  const [revRange, setRevRange] = useState("6m");
  const [now, setNow] = useState(() => new Date());

  const reload = useCallback(() => {
    Promise.allSettled([
      axios.get(`${API}/clients`).then(({ data }) => setClients(data)),
      axios.get(`${API}/projects`).then(({ data }) => setProjects(data)),
      axios.get(`${API}/orders`).then(({ data }) => setOrders(data)).catch(() => setOrders([])),
    ]).then(() => setLoaded(true));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 20000); return () => clearInterval(t); }, []);

  const projByClient = useMemo(() => {
    const m = {}; for (const p of projects) (m[p.client_id] = m[p.client_id] || []).push(p); return m;
  }, [projects]);
  const ordByClient = useMemo(() => {
    const m = {}; for (const o of orders) { const k = o.client_id; if (k) (m[k] = m[k] || []).push(o); } return m;
  }, [orders]);

  const metricsFor = useCallback((c) => {
    const ps = projByClient[c.id] || [];
    const os = ordByClient[c.id] || [];
    const quoted = ps.filter((p) => p.quote);
    return {
      projects: ps.length,
      quotes: quoted.length,
      orders: os.length,
      openQuotes: quoted.filter((p) => p.quote && p.quote.status !== "approved").length,
      revenue: os.reduce((s, o) => s + Number(o.total || 0), 0) || quoted.reduce((s, p) => s + Number(p.quote?.total || 0), 0),
      createdAt: c.created_at || c.createdAt || "",
    };
  }, [projByClient, ordByClient]);

  // KPIs
  const totalClients = clients.length;
  const activeProjects = projects.length;
  const openQuotes = projects.filter((p) => p.quote && p.quote.status !== "approved").length;

  // Local time (Europe/Tirane, matching the prototype)
  const localTime = tzTime("Europe/Tirane", now);
  const tParts = (() => {
    try {
      const ps = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Tirane", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now);
      const hh = +(ps.find((p) => p.type === "hour")?.value || 0);
      const mm = +(ps.find((p) => p.type === "minute")?.value || 0);
      return { hh, mm };
    } catch { return { hh: 0, mm: 0 }; }
  })();

  // Top clients by revenue
  const topClients = useMemo(() => clients
    .map((c) => ({ id: c.id, name: c.name, total: metricsFor(c).revenue }))
    .filter((c) => c.total > 0).sort((a, b) => b.total - a.total).slice(0, 5), [clients, metricsFor]);
  const revenueTotal = topClients.reduce((s, c) => s + c.total, 0) || orders.reduce((s, o) => s + Number(o.total || 0), 0);

  // Filter + sort the visible client list
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = clients.filter((c) =>
      !q || (c.name || "").toLowerCase().includes(q) || (c.city || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
    if (filterKey !== "all") {
      list = list.filter((c) => {
        const m = metricsFor(c);
        if (filterKey === "open") return m.openQuotes > 0;
        if (filterKey === "orders") return m.orders > 0;
        if (filterKey === "projects") return m.projects > 0;
        if (filterKey === "leads") return m.projects === 0;
        return true;
      });
    }
    const nm = (c) => (c.name || "").toLowerCase();
    return list.slice().sort((a, b) => {
      const ma = metricsFor(a), mb = metricsFor(b);
      switch (sortKey) {
        case "name_az": return nm(a) < nm(b) ? -1 : nm(a) > nm(b) ? 1 : 0;
        case "name_za": return nm(a) > nm(b) ? -1 : nm(a) < nm(b) ? 1 : 0;
        case "projects": return mb.projects - ma.projects;
        case "revenue": return mb.revenue - ma.revenue;
        default: return String(mb.createdAt).localeCompare(String(ma.createdAt));
      }
    });
  }, [clients, search, filterKey, sortKey, metricsFor]);

  // Revenue area chart — real monthly order totals over the selected range
  const revMonths = revRange === "12m" ? 12 : revRange === "3m" ? 3 : 6;
  const revenueData = useMemo(() => monthlyMoney(orders, revMonths, "total", "created_at"), [orders, revMonths]);
  const revenueChartTotal = revenueData.reduce((s, m) => s + m.value, 0);

  // Real trends (30d vs prior 30d) + monthly sparklines for the KPI cards
  const clientsTrend = useMemo(() => trendOf(clients), [clients]);
  const projectsTrend = useMemo(() => trendOf(projects), [projects]);
  const quotesTrend = useMemo(() => trendOf(projects.filter((p) => p.quote)), [projects]);
  // audit 2026-07-18: SPARK_FALLBACK (fake demo sparkline) removed — an empty
  // account shows NO sparkline (real empty state), never invented data.
  const sparkClients = useMemo(() => { const v = monthlySeries(clients); return v.some((x) => x) ? v : []; }, [clients]);
  const sparkProjects = useMemo(() => { const v = monthlySeries(projects); return v.some((x) => x) ? v : []; }, [projects]);
  const sparkQuotes = useMemo(() => { const v = monthlySeries(projects.filter((p) => p.quote)); return v.some((x) => x) ? v : []; }, [projects]);

  const createClient = async (form) => {
    const { data } = await axios.post(`${API}/clients`, form);
    setClients((c) => [data, ...c]); setShowCreate(false);
  };
  const deleteClient = async (id) => {
    if (!(await uiConfirm("Delete this client and all its projects?"))) return;
    await axios.delete(`${API}/clients/${id}`).catch(() => {});
    setClients((c) => c.filter((x) => x.id !== id)); setCardMenu(null);
  };
  const [renameFor, setRenameFor] = useState(null); // client being renamed (premium dialog)
  const renameClient = (c) => { setCardMenu(null); setRenameFor(c); };
  const saveRename = async (name) => {
    const c = renameFor;
    const { data } = await axios.put(`${API}/clients/${c.id}`, { ...c, name });
    setClients((cs) => cs.map((x) => (x.id === c.id ? data : x)));
    setRenameFor(null);
  };

  return (
    <>
      <LhcHeader search={search} onSearch={setSearch} />
      <section className="cx-page cx-scope" data-testid="store-app">
        <div className="cx-wrap">

          {/* Page header */}
          <div className="cx-head">
            <div className="cx-head-l">
              <div className="cx-head-ic">{Icon.users(24)}</div>
              <div>
                <h1 className="cx-title">Clients</h1>
                <p className="cx-sub">Manage your clients, projects and quotes in one place.</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Timeline entry (Stage C §5.4) — prototype filter-btn markup, lhc-app.html:16065 */}
              <button className="filter-btn" onClick={() => nav("/store/timeline")} style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="timeline-btn">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                Timeline
              </button>
              <button className="cx-btn cx-btn-primary" id="home-new-client" onClick={() => setShowCreate(true)} data-testid="new-client">
                {Icon.plus(15)}<span>{t("store.newClient")}</span>
              </button>
            </div>
          </div>

          {/* KPI stat cards */}
          <div className="cx-stats">
            <DashboardStatCard loading={!loaded} tone="blue" icon={Icon.users(22)} label={t("store.totalClients")} value={totalClients} sub={t("store.activeAccounts")} trend={clientsTrend} spark={sparkClients} sparkColor="#2D7FF9" testId="stat-clients" />
            <DashboardStatCard loading={!loaded} tone="green" icon={Icon.folder(22)} label={t("store.activeProjects")} value={activeProjects} sub={t("store.configurations")} trend={projectsTrend} spark={sparkProjects} sparkColor="#16A34A" testId="stat-projects" />
            <DashboardStatCard loading={!loaded} tone="purple" icon={Icon.doc(22)} label={t("store.openQuotes")} value={openQuotes} sub={t("store.pendingApproval")} trend={quotesTrend} spark={sparkQuotes} sparkColor="#7C3AED" testId="stat-quotes" />
            <DashboardStatCard tone="amber" icon={Icon.clock(22)} label={t("store.localTime")} value={localTime} sub="Europe/Tirane" clock={<ClockDial hh={tParts.hh} mm={tParts.mm} />} />
          </div>

          {/* Revenue overview + top clients */}
          <div className="cx-rev-row">
            <div className="cx-card cx-rev">
              <div className="cx-rev-head">
                <div>
                  <div className="cx-rev-t">Revenue overview</div>
                  <div className="cx-rev-st">Quote value by month ({REV_RANGES.find((r) => r.key === revRange)?.label.toLowerCase()})</div>
                </div>
                <Dropdown caption={null} options={REV_RANGES} value={revRange} onPick={setRevRange} />
              </div>
              {loaded ? (
                <Reveal>
                  <div className="cx-rev-total">{money0(revenueChartTotal || revenueTotal)}</div>
                  <RevenueChart data={revenueData} />
                </Reveal>
              ) : (
                <>
                  <Sk w={150} h={28} r={8} style={{ margin: "2px 0 12px", display: "block" }} />
                  <SkChart h={210} />
                </>
              )}
            </div>
            <TopClientsCard
              loading={!loaded}
              title="Top clients"
              items={topClients.map((c) => ({ id: c.id, name: c.name, value: c.total }))}
              onItem={(it) => nav(`/store/clients/${it.id}`)}
              onViewAll={() => { setFilterKey("all"); setSortKey("revenue"); }}
              viewAllLabel="View all clients"
              emptyHint="No quote data yet."
            />
          </div>

          {/* Toolbar */}
          <ClientToolbar
            search={search} onSearch={setSearch} searchPlaceholder="Search clients by name, email or city…" searchTestId="home-search-input"
            filterKey={filterKey} filterOptions={HOME_FILTERS} onFilter={setFilterKey}
            sortKey={sortKey} sortOptions={HOME_SORTS} onSort={setSortKey}
            view={view} onView={setView}
            count={loaded ? visible.length : null} countLabel="client"
          />

          {/* Clients grid — skeleton cards while loading, real cards fade in */}
          {!loaded ? (
            <div className={`cx-grid ${view === "list" ? "is-list" : ""}`} data-testid="clients-grid" data-loading="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="cx-card" style={{ overflow: "hidden" }}>
                  <Sk w="100%" h={236} r={0} style={{ display: "block" }} />
                </div>
              ))}
            </div>
          ) : (
          <div className={`cx-grid lhc-reveal ${view === "list" ? "is-list" : ""}`} data-testid="clients-grid">
            {visible.map((c) => {
              const m = metricsFor(c);
              const country = CITY_COUNTRY[c.city] || "";
              const tz = CITY_TZ[c.city] || "Europe/Tirane";
              const metaItems = [
                { icon: Icon.pin(12), text: c.city ? (country ? `${c.city}, ${country}` : c.city) : (country || "—") },
                { icon: Icon.clock(12), text: tzTime(tz, now) },
              ];
              return (
                <ClientCard
                  key={c.id}
                  client={c}
                  gradient={heroGradientFor(c.id)}
                  metrics={m}
                  metaItems={metaItems}
                  menuOpen={cardMenu === c.id}
                  onMenuToggle={(open) => setCardMenu(open ? c.id : null)}
                  menuItems={[
                    { label: "Open client", icon: Icon.doc(14), onClick: () => nav(`/store/clients/${c.id}`) },
                    { label: "Rename", icon: Icon.edit(14), onClick: () => renameClient(c) },
                    { sep: true },
                    { label: "Delete client", icon: Icon.trash(14), danger: true, onClick: () => deleteClient(c.id) },
                  ]}
                  onOpen={() => nav(`/store/clients/${c.id}`)}
                />
              );
            })}

            {/* Add new client tile */}
            {view !== "list" && (
              <div className="cx-cc-add" onClick={() => setShowCreate(true)} data-testid="add-client-tile">
                <div className="cx-cc-add-plus">{Icon.plus(20)}</div>
                <div className="cx-cc-add-title">Add new client</div>
                <div className="cx-cc-add-sub">Create a new client profile</div>
              </div>
            )}
          </div>
          )}
        </div>
      </section>

      {showCreate && <NewClientModal onClose={() => setShowCreate(false)} onCreate={createClient} />}
      {renameFor && <RenameClientModal initial={renameFor.name} onClose={() => setRenameFor(null)} onSave={saveRename} />}
    </>
  );
}

/* New-client modal — rich form matching the prototype (Basic info + Location). */
const COUNTRIES = [
  ["CH", "Switzerland"], ["IT", "Italy"], ["FR", "France"], ["DE", "Germany"], ["AT", "Austria"],
  ["ES", "Spain"], ["PT", "Portugal"], ["NL", "Netherlands"], ["BE", "Belgium"], ["GB", "United Kingdom"],
  ["AL", "Albania"], ["GR", "Greece"], ["US", "United States"],
];
function NewClientModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", website: "", phone: "", vat_number: "", country: "", city: "", address: "", postal_code: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); if (!form.name.trim()) return; setBusy(true); setErr("");
    try { await onCreate(form); } catch { setErr("Could not create client"); setBusy(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 20px", overflowY: "auto" }} data-testid="new-client-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ width: 600, maxWidth: "100%", background: "var(--bg-canvas)", border: "0.5px solid var(--chrome-border)", borderRadius: 16, padding: 24, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-.02em" }}>New client</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 2 }}>Add a new client to start working together.</div>
        </div>

        <div className="nc-card">
          <div className="nc-card-head"><h2 className="nc-card-title">Basic information</h2></div>
          <div className="nc-row">
            <div className="nc-field"><label className="nc-label">Client name <span className="nc-req">*</span></label><input className="nc-input" autoFocus value={form.name} onChange={set("name")} data-testid="client-name" placeholder="e.g. Acme Interiors" /></div>
            <div className="nc-field"><label className="nc-label">Company name</label><input className="nc-input" value={form.company} onChange={set("company")} placeholder="e.g. Acme Ltd." /></div>
          </div>
          <div className="nc-row">
            <div className="nc-field"><label className="nc-label">Email</label><input className="nc-input" type="email" value={form.email} onChange={set("email")} data-testid="client-email" placeholder="info@acme.com" /></div>
            <div className="nc-field"><label className="nc-label">Website</label><input className="nc-input" value={form.website} onChange={set("website")} placeholder="www.acme.com" /></div>
          </div>
          <div className="nc-row">
            <div className="nc-field"><label className="nc-label">Phone</label><input className="nc-input" type="tel" value={form.phone} onChange={set("phone")} data-testid="client-phone" placeholder="+41 76 123 45 67" /></div>
            <div className="nc-field"><label className="nc-label">Company ID</label><input className="nc-input" value={form.vat_number} onChange={set("vat_number")} placeholder="CHE-123.456.789" /></div>
          </div>
        </div>

        <div className="nc-card">
          <div className="nc-card-head">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 22s-7-7.5-7-13a7 7 0 1 1 14 0c0 5.5-7 13-7 13z" /><circle cx="12" cy="9" r="2.5" /></svg>
            <h2 className="nc-card-title">Location</h2>
          </div>
          <div className="nc-row">
            <div className="nc-field"><label className="nc-label">Country</label>
              <select className="nc-input nc-select" value={form.country} onChange={set("country")}>
                <option value="">Select country</option>
                {COUNTRIES.map(([c, n]) => <option key={c} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="nc-field"><label className="nc-label">City</label><input className="nc-input" value={form.city} onChange={set("city")} data-testid="client-city" placeholder="City" /></div>
          </div>
          <div className="nc-row">
            <div className="nc-field"><label className="nc-label">Address</label><input className="nc-input" value={form.address} onChange={set("address")} placeholder="Street address" /></div>
            <div className="nc-field"><label className="nc-label">Postal code</label><input className="nc-input" value={form.postal_code} onChange={set("postal_code")} placeholder="8001" /></div>
          </div>
        </div>

        {err && <div style={{ fontSize: 12.5, color: "#dc2626" }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} className="nc-btn nc-btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} className="nc-btn nc-btn-primary" data-testid="client-create">{busy ? "Saving…" : "Create client"}</button>
        </div>
      </form>
    </div>
  );
}

/* Client notes — backed by the generic chat thread on entity_type "client". */
function ClientNotes({ clientId }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/chat/client/${clientId}/messages`).then(({ data }) => setMessages(data)).catch(() => setMessages([]));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return;
    const { data } = await axios.post(`${API}/chat/client/${clientId}/messages`, { body });
    setMessages((m) => [...m, data]); setBody("");
  };
  return (
    <div className="tab-content tab-notes" data-tab="notes" style={{ display: "block", paddingTop: 8 }}>
      <div className="nc-card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <h3 className="nc-card-title">Notes &amp; comments</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 380, overflowY: "auto" }} data-testid="client-notes">
          {messages.length === 0 ? <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>No notes yet. Add the first note about this client.</div> :
            messages.map((m) => (
              <div key={m.id} style={{ background: "var(--bg-elevated)", border: "0.5px solid var(--chrome-border)", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 3 }}>{m.author_name} · {String(m.created_at).slice(0, 16).replace("T", " ")}</div>
                <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{m.body}</div>
              </div>
            ))}
        </div>
        <form onSubmit={send} style={{ display: "flex", gap: 8 }}>
          <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a note…" data-testid="note-input" style={{ flex: 1, height: 40, padding: "0 12px", border: "1px solid var(--border-strong)", borderRadius: 9, background: "var(--bg-surface)", color: "var(--text-primary)", fontFamily: "var(--font)", fontSize: 13, outline: "none" }} />
          <button className="nc-btn nc-btn-primary" data-testid="note-send">Add note</button>
        </form>
      </div>
    </div>
  );
}

/* ───────────────────────── shell (detail/report pages — legacy, ported later) ───────────────────────── */
function Shell({ children, title }) {
  const t = useT();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const b = user?.branding_context || {};
  const initials = (b.dealer_name || user?.name || "LH").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const onClients = loc.pathname.startsWith("/store/clients") || loc.pathname === "/store";
  const feats = user?.allowed_features || [];
  const has3D = feats.includes("configurator_3d") || feats.includes("configurator");
  return (
    <div className="st-shell" data-testid="store-app">
      <aside className="st-side">
        <div className="st-brand" data-testid="store-brand">
          <div className="st-brand-mark">{initials}</div>
          <div>
            <div className="st-brand-name">{b.dealer_name || b.company_name || "LHC Studio"}</div>
            <div className="st-brand-sub">Dealer workspace</div>
          </div>
        </div>
        <nav className="st-nav">
          <Link to="/store/clients" className={`st-nav-i ${onClients ? "on" : ""}`} data-testid="nav-clients">{t("nav.clientsProjects")}</Link>
          {has3D && <Link to="/studio" className="st-nav-i" data-testid="nav-configurator">{t("nav.configurator")}</Link>}
        </nav>
        <div className="st-side-foot">
          <div className="st-user">{user?.name}<span>{user?.role}</span></div>
          <button className="st-signout" onClick={() => logout().finally(() => nav("/login"))} data-testid="store-signout">Sign out</button>
        </div>
      </aside>
      <main className="st-main">
        <header className="st-top"><h1>{title}</h1></header>
        <div className="st-content">{children}</div>
      </main>
    </div>
  );
}

/* ───────────────────────── client detail (prototype "orders-page") ───────────────────────── */
const PSTATUS = {
  draft: { label: "Draft", color: "#6b7280", progress: 15 },
  quoted: { label: "Quoted", color: "#2563eb", progress: 45 },
  sales_order: { label: "Sales order", color: "#3b82f6", progress: 60 },
  approved: { label: "Approved", color: "#10b981", progress: 70 },
  confirmed: { label: "Confirmed", color: "#8b5cf6", progress: 80 },
  in_production: { label: "In production", color: "#f59e0b", progress: 90 },
  ready: { label: "Ready", color: "#0ea5e9", progress: 95 },
  delivered: { label: "Delivered", color: "#10b981", progress: 100 },
};
const pstat = (s) => PSTATUS[s] || { label: s || "Draft", color: "#6b7280", progress: 25 };

const P_STATUS_FILTERS = [
  { key: "all", label: "All status" },
  { key: "draft", label: "Draft" },
  { key: "quoted", label: "Quoted" },
  { key: "approved", label: "Approved" },
  { key: "in_production", label: "In production" },
  { key: "delivered", label: "Delivered" },
];
const P_SORTS = [
  { key: "recent", label: "Recent" },
  { key: "name", label: "Name" },
  { key: "value", label: "Value" },
];
const projValueOf = (p) => Number(p.quote?.total ?? p.price ?? 0);
const projModulesOf = (p) => (p.design?.cabinets?.length) || (p.design?._native?.placements?.length) || 0;
function seriesTrend(d) {
  if (!d || d.length < 2) return null;
  const h = Math.floor(d.length / 2);
  const a = d.slice(0, h).reduce((s, x) => s + x.value, 0);
  const b = d.slice(h).reduce((s, x) => s + x.value, 0);
  if (a <= 0) return b > 0 ? { dir: "up", pct: 100 } : null;
  return { dir: b >= a ? "up" : "down", pct: Math.round(((b - a) / a) * 100) };
}
const P_PAGE_SIZE = 6;

const CLIENT_TAB_KEYS = ["projects", "quotes", "orders", "notes", "activity", "overview"];

function ClientDetailPage() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  // ?tab=quotes deep link — Create Quotation in /studio lands straight on the Quotes tab.
  // ?tab= is now READ AND WRITTEN (2026-08-24 "where I am must survive a
  // refresh") — the deep link from /studio still works unchanged.
  const [tab, setTab] = useViewParam("tab", "projects", CLIENT_TAB_KEYS);
  const [quoteRows, setQuoteRows] = useState(null); // db.quotes meta list (null = endpoint unavailable → legacy mirrors)
  const [quotesErr, setQuotesErr] = useState(false); // RC polish: a failed quotes fetch shows error+Retry, never silent fallback
  // three-state loading (2026-07-18): skeletons until the initial fetches
  // settle — never a "—"/0/€0 ghost client before the data exists.
  const [loaded, setLoaded] = useState(false);
  // premium toast (2026-07-18): quote-action failures surface as an in-app
  // toast instead of a native browser alert().
  const [flash, setFlash] = useState("");
  const flashRef = useRef(null);
  const toast = useCallback((m) => {
    setFlash(m);
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setFlash(""), 2800);
  }, []);
  useEffect(() => () => clearTimeout(flashRef.current), []);
  const [pSearch, setPSearch] = useState("");
  const [pStatus, setPStatus] = useState("all");
  const [pSort, setPSort] = useState("recent");
  const [pView, setPView] = useState("grid");
  const [page, setPage] = useState(1);
  const [sideRange, setSideRange] = useState("6m");
  const [creating, setCreating] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const load = useCallback(() => {
    Promise.allSettled([
      axios.get(`${API}/clients`).then(({ data }) => setClient(data.find((c) => c.id === clientId) || null)),
      axios.get(`${API}/projects?client_id=${clientId}`).then(({ data }) => setProjects(data)),
      axios.get(`${API}/orders`).then(({ data }) => setOrders((data || []).filter((o) => o.client_id === clientId))).catch(() => setOrders([])),
      axios.get(`${API}/quotes`, { params: { client_id: clientId, limit: 500 } })
        .then(({ data }) => { setQuoteRows(Array.isArray(data) ? data : []); setQuotesErr(false); })
        .catch(() => { setQuoteRows(null); setQuotesErr(true); }),
    ]).then(() => setLoaded(true));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  // "New project" opens the wizard IMMEDIATELY (no name prompt): the project is
  // born with a placeholder name and the wizard's final Name field renames it
  // (reactEngineWin.finishWizard / engineHandoff.finishWizard PUT {name}).
  // `from` lets the wizard's Back return to this client page.
  const createProject = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { name: NEW_PROJECT_PLACEHOLDER, client_id: clientId });
      // Launch the React-ported wizard flow (Model → Materials → Assembly → Room → Build → configurator).
      // Direct to the models screen (spec §1) — `?start=wizard` stays as a back-compat redirect.
      nav(`/studio/${data.id}/models?cat=kitchen`, { state: { from: `/store/clients/${clientId}`, newProject: true } });
    } catch (_) { toast("Could not create the project."); setCreating(false); }
  };
  const deleteProject = async (id) => {
    if (!(await uiConfirm("Delete this project?"))) return;
    await axios.delete(`${API}/projects/${id}`).catch(() => {});
    setProjects((p) => p.filter((x) => x.id !== id));
  };
  const [renameOpen, setRenameOpen] = useState(false); // premium rename dialog
  const editClient = () => { setMoreOpen(false); if (client) setRenameOpen(true); };
  const saveClientName = async (name) => {
    const { data } = await axios.put(`${API}/clients/${client.id}`, { ...client, name });
    setClient(data);
    setRenameOpen(false);
  };
  const deleteClientDetail = async () => {
    setMoreOpen(false);
    if (!client) return;
    if (!(await uiConfirm("Delete this client and all its projects?"))) return;
    await axios.delete(`${API}/clients/${client.id}`).catch(() => {});
    nav("/store");
  };

  // Quotes tab reads db.quotes — every revision of every project (unlimited
  // quotes/orders, 2026-07-11). Quotes CONVERTED to an order leave this list
  // (2026-07-13 user flow: "Add as Order" moves it to the Orders tab — it must
  // not keep showing here). Legacy per-project mirrors are the fallback shape.
  const projNameById = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const quotes = (quoteRows
    ? quoteRows.map((q) => ({ ...q, project: projNameById[q.project_id] || (q.customer && q.customer.name) || "—" }))
    : projects.filter((p) => p.quote).map((p) => ({ ...p.quote, project: p.name, project_id: p.id }))
  ).filter((q) => q.status !== "converted");
  const revenue = orders.reduce((s, o) => s + Number(o.total || 0), 0) || quotes.reduce((s, q) => s + Number(q.total || 0), 0);
  const country = client ? (CITY_COUNTRY[client.city] || "") : "";
  const activity = [
    ...projects.map((p) => ({ ts: p.created_at, kind: "project", text: `Project “${p.name}” created` })),
    ...orders.map((o) => ({ ts: o.created_at, kind: "order", text: `Order ${o.number} — ${o.status}` })),
  ].filter((a) => a.ts).sort((a, b) => String(b.ts).localeCompare(String(a.ts)));

  /* projects: filter → sort → paginate */
  const filteredProjects = projects.filter((p) =>
    (!pSearch || (p.name || "").toLowerCase().includes(pSearch.toLowerCase())) &&
    (pStatus === "all" || (p.status || "draft") === pStatus));
  const sortedProjects = filteredProjects.slice().sort((a, b) => {
    if (pSort === "name") return (a.name || "").localeCompare(b.name || "");
    if (pSort === "value") return projValueOf(b) - projValueOf(a);
    return String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""));
  });
  const pageCount = Math.max(1, Math.ceil(sortedProjects.length / P_PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const pagedProjects = sortedProjects.slice((curPage - 1) * P_PAGE_SIZE, curPage * P_PAGE_SIZE);
  const pagerNums = (() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const out = [1];
    const a = Math.max(2, curPage - 1), b = Math.min(pageCount - 1, curPage + 1);
    if (a > 2) out.push("…");
    for (let i = a; i <= b; i++) out.push(i);
    if (b < pageCount - 1) out.push("…");
    out.push(pageCount);
    return out;
  })();

  /* summary + side panel data (all real, with safe fallbacks) */
  const projValues = projects.map(projValueOf).filter((v) => v > 0);
  const avgValue = projValues.length ? projValues.reduce((s, v) => s + v, 0) / projValues.length : 0;
  const topProjects = projects.map((p) => ({ id: p.id, name: p.name, value: projValueOf(p) }))
    .sort((a, b) => b.value - a.value).slice(0, 5);
  const sideMonths = sideRange === "12m" ? 12 : sideRange === "3m" ? 3 : 6;
  const revSource = orders.length ? orders : projects.filter((p) => p.quote).map((p) => ({ created_at: p.updated_at || p.created_at, total: Number(p.quote.total || 0) }));
  const clientRevData = monthlyMoney(revSource, sideMonths, "total", "created_at");
  const clientRevTrend = seriesTrend(clientRevData);

  const summaryRows = [
    { tone: "blue", icon: Icon.folder(16), label: "Total projects", value: projects.length },
    { tone: "purple", icon: Icon.doc(16), label: "With a quote", value: quotes.length },
    { tone: "green", icon: Icon.receipt(16), label: "Orders", value: orders.length },
    { tone: "amber", icon: Icon.euro(16), label: "Total revenue", value: money0(revenue) },
    { tone: "blue", icon: Icon.chart(16), label: "Average project value", value: money0(avgValue) },
  ];

  const metaItems = [];
  if (client?.email) metaItems.push({ icon: Icon.mail(13), text: client.email });
  if (client?.phone) metaItems.push({ icon: Icon.phone(13), text: client.phone });
  if (client?.city) metaItems.push({ icon: Icon.pin(13), text: country ? `${client.city}, ${country}` : client.city });
  if (client?.created_at) metaItems.push({ icon: Icon.cal(13), text: `Client since ${fmtDate(client.created_at)}` });

  const TABS = [
    { key: "projects", label: "Projects" }, { key: "quotes", label: "Quotes" }, { key: "orders", label: "Orders" },
    { key: "notes", label: "Notes" }, { key: "activity", label: "Activity" }, { key: "overview", label: "Overview" },
  ];

  const headerActions = (
    <>
      <button className="cx-btn cx-btn-primary" onClick={createProject} disabled={creating} data-testid="new-project">{Icon.plus(15)}<span>{creating ? "Creating…" : "New project"}</span></button>
      <button className="cx-btn cx-btn-ghost" onClick={editClient}>{Icon.edit(14)}<span>Edit client</span></button>
      <ActionMenu open={moreOpen} onToggle={setMoreOpen} btnClassName="cx-btn cx-btn-ghost" label={<span>More</span>}
        items={[
          { label: "Rename client", icon: Icon.edit(14), onClick: editClient },
          /* Timeline entry (Stage C §5.4) — prototype client ⋮ menu item, lhc-app.html:42405-42408 */
          { label: "View in timeline", icon: Icon.cal(14), onClick: () => { setMoreOpen(false); nav("/store/timeline"); } },
          { sep: true },
          { label: "Delete client", icon: Icon.trash(14), danger: true, onClick: deleteClientDetail },
        ]} />
    </>
  );

  return (
    <>
      <LhcHeader showSearch={false} />
      <section className="cx-page cx-scope" data-testid="client-detail">
        <div className="cx-wrap">
          <button className="cx-back" onClick={() => nav("/store")} data-testid="orders-back">{Icon.back(15)}<span>Back to clients</span></button>

          <ClientHeader loading={!loaded} name={client?.name} gradient={client ? heroGradientFor(client.id) : "var(--cx-bg)"} metaItems={metaItems} actions={headerActions} />

          <ClientTabs tabs={TABS} active={tab} onChange={setTab} />

          {/* Projects tab */}
          {tab === "projects" && (
            <div className="cx-pbody">
              <div className="cx-pmain">
                <div className="cx-pmain-head">
                  <span className="cx-pmain-t">Projects</span>
                  <span className="cx-pmain-badge">{loaded ? projects.length : <Sk w={16} h={12} r={4} />}</span>
                </div>
                <div className="cx-ptoolbar">
                  <SearchInput value={pSearch} onChange={(v) => { setPSearch(v); setPage(1); }} placeholder="Search projects…" />
                  <Dropdown caption={null} options={P_STATUS_FILTERS} value={pStatus} onPick={(k) => { setPStatus(k); setPage(1); }} />
                  <Dropdown caption="Sort by" options={P_SORTS} value={pSort} onPick={setPSort} twoLine />
                  <ViewToggle value={pView} onChange={setPView} />
                </div>
                <div className={`cx-pgrid ${pView === "list" ? "is-list" : ""}`} {...(!loaded ? { "data-loading": "true" } : {})}>
                  {!loaded ? (
                    [0, 1].map((i) => (
                      <div key={i} className="cx-card" style={{ overflow: "hidden" }}>
                        <Sk w="100%" h={168} r={0} style={{ display: "block" }} />
                      </div>
                    ))
                  ) : pagedProjects.length === 0 ? (
                    <div style={{ gridColumn: "1/-1" }}>
                      <EmptyState icon={Icon.folder(22)} title="No projects yet" hint="Create the first project to start configuring a kitchen for this client."
                        action={<button className="cx-btn cx-btn-primary" onClick={createProject} disabled={creating} style={{ marginTop: 6 }}>{Icon.plus(15)}<span>{creating ? "Creating…" : "New project"}</span></button>} />
                    </div>
                  ) : pagedProjects.map((p) => (
                    <ProjectCard key={p.id} project={p} status={pstat(p.status)} value={projValueOf(p)} modules={projModulesOf(p)}
                      updated={fmtDate(p.updated_at || p.created_at)} onOpen={() => nav(`/studio/${p.id}`)} onDelete={() => deleteProject(p.id)} />
                  ))}
                </div>
                {pageCount > 1 && (
                  <div className="cx-pager">
                    <button className="cx-pager-btn" disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>{Icon.chevL(15)}</button>
                    {pagerNums.map((n, i) => n === "…"
                      ? <span key={`d${i}`} className="cx-pager-dots">…</span>
                      : <button key={n} className={`cx-pager-btn ${n === curPage ? "active" : ""}`} onClick={() => setPage(n)}>{n}</button>)}
                    <button className="cx-pager-btn" disabled={curPage === pageCount} onClick={() => setPage(curPage + 1)}>{Icon.chevR(15)}</button>
                  </div>
                )}
              </div>

              <ProjectSummaryPanel
                loading={!loaded}
                rows={summaryRows}
                dropdown={<Dropdown caption={null} options={REV_RANGES} value={sideRange} onPick={setSideRange} />}
                revenue={{ total: revenue, trend: clientRevTrend, data: clientRevData }}
                topProjects={topProjects}
                onTopItem={(it) => nav(`/studio/${it.id}`)}
                onViewFull={() => setTab("overview")}
              />
            </div>
          )}

          {/* Quotes tab — db.quotes rows with Send / Add as Order / Delete (2026-07-11) */}
          {tab === "quotes" && !loaded && (
            <div className="cx-card" style={{ padding: 0, overflow: "hidden" }} data-loading="true">
              <div style={{ display: "flex", gap: 28, alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--cx-border)" }}>
                {["Total", "Approved", "Pending"].map((l) => (
                  <div key={l}><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>{l}</div><Sk w={34} h={19} r={6} style={{ marginTop: 4, display: "block" }} /></div>
                ))}
                <div style={{ marginLeft: "auto", textAlign: "right" }}><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>Value</div><Sk w={78} h={19} r={6} style={{ marginTop: 4 }} /></div>
              </div>
              <div style={{ padding: 20 }}><SkRows n={4} h={42} gap={10} /></div>
            </div>
          )}
          {/* RC polish: a failed canonical quotes fetch shows error + Retry —
              never a silent fallback to possibly-stale mirror data. */}
          {tab === "quotes" && loaded && quotesErr && (
            <div className="cx-card" style={{ padding: 28, textAlign: "center" }} data-testid="quotes-error">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Couldn't load quotes</div>
              <div style={{ fontSize: 13, color: "var(--cx-muted)", marginBottom: 14 }}>Check your connection, then try again.</div>
              <button className="cx-btn cx-btn-primary" onClick={() => { setLoaded(false); load(); }}>Retry</button>
            </div>
          )}
          {tab === "quotes" && loaded && !quotesErr && (() => {
            const QPILL = {
              draft: { label: "Draft", bg: "var(--cx-blue-soft)", fg: "var(--cx-blue)" },
              sent: { label: "Sent", bg: "var(--cx-purple-soft)", fg: "var(--cx-purple)" },
              approved: { label: "Approved", bg: "var(--cx-green-soft)", fg: "var(--cx-green)" },
              converted: { label: "Ordered", bg: "var(--cx-green-soft)", fg: "var(--cx-green)" },
              rejected: { label: "Rejected", bg: "var(--cx-amber-soft)", fg: "var(--cx-amber)" },
              changes_requested: { label: "Changes", bg: "var(--cx-amber-soft)", fg: "var(--cx-amber)" },
            };
            const qpill = (s) => QPILL[s] || QPILL.draft;
            const approvedN = quotes.filter((q) => q.status === "approved" || q.status === "converted").length;
            const sendQuote = async (q) => {
              try { await axios.post(`${API}/quotes/${q.id}/send`); load(); }
              catch (err) { toast(err?.response?.data?.detail || "Could not send quote"); }
            };
            const deleteQuote = async (q) => {
              if (!(await uiConfirm(`Delete quote ${q.number}?`))) return;
              try { await axios.delete(`${API}/quotes/${q.id}`); load(); }
              catch (err) { toast(err?.response?.data?.detail || "Could not delete quote"); }
            };
            const orderQuote = async (q) => {
              try {
                await axios.post(`${API}/sales-orders/from-project/${q.project_id}`, q.id ? { quote_id: q.id } : {});
                setTab("orders"); load();
              } catch (err) { toast(err?.response?.data?.detail || "Could not create order"); }
            };
            return (
            <div className="cx-card lhc-reveal" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 28, alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--cx-border)" }}>
                <div><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>Total</div><div style={{ fontSize: 19, fontWeight: 800 }}>{quotes.length}</div></div>
                <div><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>Approved</div><div style={{ fontSize: 19, fontWeight: 800, color: "var(--cx-green)" }}>{approvedN}</div></div>
                <div><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>Pending</div><div style={{ fontSize: 19, fontWeight: 800, color: "var(--cx-blue)" }}>{quotes.length - approvedN}</div></div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}><div style={{ fontSize: 11, color: "var(--cx-faint)", textTransform: "uppercase", letterSpacing: ".04em" }}>Value</div><div style={{ fontSize: 19, fontWeight: 800 }}>{money0(quotes.reduce((s, q) => s + Number(q.total || 0), 0))}</div></div>
              </div>
              {quotes.length === 0 ? <EmptyState icon={Icon.doc(22)} title="No quotes yet" hint="Quotes appear here once a project has been priced and quoted." /> : (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr .9fr .7fr 1.6fr", padding: "10px 20px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--cx-faint)", borderBottom: "1px solid var(--cx-border)" }}>
                    <div>Quote ID</div><div>Project</div><div>Amount</div><div style={{ textAlign: "right" }}>Status</div>
                  </div>
                  {quotes.map((q) => (
                    <div key={q.id || q.number} data-testid="quote-row" title="Open the project this quote belongs to"
                      onClick={() => q.project_id && nav(`/studio/${q.project_id}`)}
                      style={{ display: "grid", gridTemplateColumns: "1fr .9fr .7fr 1.6fr", alignItems: "center", padding: "13px 20px", borderBottom: "1px solid var(--cx-border-soft)", fontSize: 13, cursor: q.project_id ? "pointer" : "default" }}>
                      <div style={{ fontWeight: 700, color: "var(--cx-ink)" }}>{q.number}{q.revision > 1 ? <span style={{ fontWeight: 600, color: "var(--cx-faint)", marginLeft: 6, fontSize: 11 }}>Rev {q.revision}</span> : null}</div>
                      <div style={{ color: "var(--cx-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.project}</div>
                      <div style={{ fontWeight: 700 }}>{money(q.total)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                        <span className="cx-pc-status" style={{ background: qpill(q.status).bg, color: qpill(q.status).fg }}>{qpill(q.status).label}</span>
                        {q.id && q.status !== "approved" && q.status !== "converted" && (
                          <button className="cx-btn cx-btn-ghost" data-testid="send-quote" title="Send this quote to the client"
                            onClick={(e) => { e.stopPropagation(); sendQuote(q); }}
                            style={{ padding: "6px 12px", fontSize: 12 }}>Send Quote</button>
                        )}
                        <button className="cx-btn cx-btn-primary" data-testid="add-as-order"
                          onClick={(e) => { e.stopPropagation(); orderQuote(q); }}
                          style={{ padding: "6px 12px", fontSize: 12 }}>Add as Order</button>
                        {q.id && (
                          <button className="cx-btn cx-btn-ghost" data-testid="delete-quote" title="Delete this quote" aria-label={`Delete quote ${q.number}`}
                            onClick={(e) => { e.stopPropagation(); deleteQuote(q); }}
                            style={{ padding: "6px 9px", fontSize: 12, color: "#ef4444" }}>{Icon.trash(14)}</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })()}

          {/* Orders tab — visual pipeline workspace (redesign 2026-07-13):
              stage pipeline + premium order cards + Store↔Sales order chat.
              Same transitions/uploads/WS refresh as before — presentation only. */}
          {tab === "orders" && (
            <OrdersWorkspace
              clientId={clientId} client={client}
              onOpenProject={(pid) => pid && nav(`/studio/${pid}`)}
              onViewQuotes={() => setTab("quotes")}
            />
          )}

          {/* Notes tab */}
          {tab === "notes" && <ClientNotes clientId={clientId} />}

          {/* Activity tab */}
          {tab === "activity" && (
            <div className="cx-card" style={{ padding: "8px 20px" }}>
              {!loaded ? <div style={{ padding: "12px 0" }}><SkRows n={4} h={38} gap={12} /></div>
              : activity.length === 0 ? <EmptyState icon={Icon.clock(22)} title="No activity yet" hint="Project and order events for this client will show up here." /> : (
                <div>
                  {activity.map((a, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 13, padding: "13px 0", borderBottom: i < activity.length - 1 ? "1px solid var(--cx-border-soft)" : "none" }}>
                      <div className="cx-sum-ic" style={{ background: a.kind === "order" ? "var(--cx-blue-soft)" : "var(--cx-bg)", color: a.kind === "order" ? "var(--cx-blue)" : "var(--cx-muted)" }}>
                        {a.kind === "order" ? Icon.receipt(15) : Icon.folder(15)}
                      </div>
                      <div style={{ flex: 1, fontSize: 13, color: "var(--cx-ink)" }}>{a.text}</div>
                      <div style={{ fontSize: 11.5, color: "var(--cx-faint)" }}>{String(a.ts).slice(0, 10)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Overview tab */}
          {tab === "overview" && (
            <div className="cx-stats" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              <div className="cx-card cx-stat"><div className="cx-stat-top"><div className="cx-ic-circle t-blue is-round">{Icon.folder(20)}</div></div><div className="cx-stat-label">Projects</div><div className="cx-stat-value">{loaded ? <span className="lhc-reveal">{projects.length}</span> : <Sk w={44} h={24} r={7} />}</div><div className="cx-stat-sub">Configurations</div></div>
              <div className="cx-card cx-stat"><div className="cx-stat-top"><div className="cx-ic-circle t-purple is-round">{Icon.doc(20)}</div></div><div className="cx-stat-label">Quotes</div><div className="cx-stat-value">{loaded ? <span className="lhc-reveal">{quotes.length}</span> : <Sk w={44} h={24} r={7} />}</div><div className="cx-stat-sub">{loaded ? `${quotes.filter((q) => q.status === "approved").length} approved` : " "}</div></div>
              <div className="cx-card cx-stat"><div className="cx-stat-top"><div className="cx-ic-circle t-green is-round">{Icon.euro(20)}</div></div><div className="cx-stat-label">Revenue</div><div className="cx-stat-value">{loaded ? <span className="lhc-reveal">{money0(revenue)}</span> : <Sk w={86} h={24} r={7} />}</div><div className="cx-stat-sub">Orders + quotes</div></div>
            </div>
          )}
        </div>
      </section>

      {flash && <div className="cx-toastbar" role="status" data-testid="cx-toast">{flash}</div>}
      {renameOpen && client && <RenameClientModal initial={client.name} onClose={() => setRenameOpen(false)} onSave={saveClientName} />}
    </>
  );
}

/* Premium rename dialog (2026-07-18 trust pass): replaces (await uiPrompt()) —
 * same rename workflow (trim, no-op on unchanged/empty), professional surface. */
function RenameClientModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const v = name.trim();
    if (!v || v === initial) { onClose(); return; }
    setBusy(true);
    try { await onSave(v); } catch { setBusy(false); }
  };
  const inputStyle = { width: "100%", boxSizing: "border-box", height: 42, padding: "0 13px", border: "1px solid var(--border-strong)", borderRadius: 9, background: "var(--bg-surface)", color: "var(--text-primary)", fontFamily: "var(--font)", fontSize: 13.5, outline: "none" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="rename-client-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ width: 400, maxWidth: "100%", background: "var(--bg-surface)", border: "0.5px solid var(--chrome-border)", borderRadius: 14, padding: 24, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-.02em" }}>Rename client</div>
        <input autoFocus data-testid="rename-client-input" placeholder="Client name *" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
          <button type="button" onClick={onClose} style={{ padding: "10px 16px", border: "1px solid var(--border-strong)", borderRadius: 9, background: "transparent", color: "var(--text-secondary)", fontFamily: "var(--font)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} className="home-new" data-testid="rename-client-save" style={{ opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ───────────────────────── project detail ───────────────────────── */
const SEVC = { critical: "#dc2626", warning: "#d97706", suggestion: "#2563eb", info: "#6b7280" };

function ProjectDetailPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [validation, setValidation] = useState(null);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/projects/${projectId}`);
    setProject(data);
    const v = await axios.post(`${API}/rules/validate`, { design: data.design || {} });
    setValidation(v.data);
    const o = await axios.get(`${API}/projects/${projectId}/orders`);
    setOrders(o.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key); setMsg("");
    try { await fn(); setMsg(ok); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail?.message || (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Action failed")); }
    finally { setBusy(""); }
  };

  if (!project) return <Shell title="Project"><div className="st-empty">Loading…</div></Shell>;
  const cabinets = (project.design?.cabinets || []);
  const q = project.quote;
  const counts = validation?.counts || {};
  const blocks = validation?.blocks_production;

  return (
    <Shell title={`Project · ${project.name}`}>
      <div className="st-report-actions">
        <Link to={`/store/clients/${project.client_id}`} className="st-back">← Client</Link>
        <Link to={`/store/projects/${projectId}/report`} className="st-btn sm ghost" data-testid="open-report">View report →</Link>
      </div>
      {msg && <div className="st-toast" data-testid="project-msg">{msg}</div>}
      <div className="st-grid">
        <section className="st-card st-span2">
          <div className="st-card-row">
            <h2 className="st-card-t">Configuration <span className="st-muted">({cabinets.length} modules)</span></h2>
            <div className="st-actions">
              {cabinets.length === 0 && (
                <button className="st-btn sm" disabled={busy === "sample"} data-testid="load-sample"
                  onClick={() => act("sample", () => axios.post(`${API}/projects/${projectId}/sample-design`), "Sample kitchen loaded")}>
                  {busy === "sample" ? "Loading…" : "Load sample kitchen"}
                </button>
              )}
              <Link className="st-btn sm ghost" to={`/studio/${projectId}`} data-testid="open-3d">Open 3D Studio →</Link>
            </div>
          </div>
          {cabinets.length === 0 ? <div className="st-empty">No modules yet. Load a sample kitchen or design in the 3D Studio.</div> : (
            <table className="st-table sm" data-testid="modules-table">
              <thead><tr><th>SKU</th><th>Type</th><th>W×D×H (mm)</th><th>Price</th></tr></thead>
              <tbody>
                {cabinets.map((c) => (
                  <tr key={c.id}><td>{c.sku}</td><td>{c.type}{c.appliance ? ` · ${c.appliance}` : ""}</td>
                    <td>{c.width_mm}×{c.depth_mm}×{c.height_mm}</td><td>{money(c.price)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="st-card">
          <h2 className="st-card-t">Pricing & quote</h2>
          <div className="st-estimate" data-testid="config-estimate">
            <span>Configuration estimate</span>
            <strong>{money(cabinets.reduce((s, c) => s + Number(c.price || 0), 0) * 1.2)}</strong>
          </div>
          <div className={`st-gate ${blocks ? "bad" : "ok"}`} data-testid="production-gate">
            {blocks ? "Critical issues block quote approval & production." : "No critical issues — ready to quote."}
          </div>
          {!q ? <div className="st-muted sm">No quote yet.</div> : (
            <div className="st-quote" data-testid="quote-box">
              <div className="st-quote-h"><strong>{q.number}</strong><span className={`st-status st-${q.status}`}>{q.status}</span></div>
              <div className="st-quote-row"><span>Subtotal</span><span>{money(q.subtotal)}</span></div>
              <div className="st-quote-row total"><span>Total</span><span>{money(q.total)}</span></div>
            </div>
          )}
          <div className="st-actions col">
            <button className="st-btn" disabled={busy === "quote" || cabinets.length === 0} data-testid="generate-quote"
              onClick={() => act("quote", () => axios.post(`${API}/projects/${projectId}/quote`), "Quote generated")}>
              {q ? "Regenerate quote" : "Generate quote"}
            </button>
            <button className="st-btn" disabled={!q || q.status === "approved" || busy === "approve"} data-testid="approve-quote"
              onClick={() => act("approve", () => axios.post(`${API}/projects/${projectId}/quote/approve`), "Quote approved")}>
              Approve quote
            </button>
            <button className="st-btn dark" disabled={!q || q.status !== "approved" || busy === "order"} data-testid="create-order"
              onClick={() => act("order", () => axios.post(`${API}/projects/${projectId}/order`), "Sales order created")}>
              Convert to order
            </button>
          </div>
        </section>

        <section className="st-card st-span3">
          <h2 className="st-card-t">Validation
            <span className="st-vchips">
              {Object.keys(SEVC).map((k) => <span key={k} style={{ color: SEVC[k] }} data-testid={`vcount-${k}`}>{counts[k] || 0} {k}</span>)}
            </span>
          </h2>
          {(validation?.issues || []).length === 0 ? <div className="st-empty">All rules pass.</div> : (
            <div className="st-issues" data-testid="issues-list">
              {(validation.issues || []).map((it) => (
                <div key={it.issue_id} className="st-issue" style={{ borderLeftColor: SEVC[it.severity] }}>
                  <div className="st-issue-h" style={{ color: SEVC[it.severity] }}>{it.severity} · {it.object_id}</div>
                  <div className="st-issue-r">{it.reason}</div>
                  <div className="st-issue-f">↳ {it.suggested_fix}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {orders.length > 0 && (
          <section className="st-card st-span3">
            <h2 className="st-card-t">Orders</h2>
            <table className="st-table sm" data-testid="orders-table">
              <thead><tr><th>Order</th><th>Status</th><th>Total</th></tr></thead>
              <tbody>{orders.map((o) => <tr key={o.id}><td>{o.number}</td><td><span className={`st-status st-${o.status}`}>{o.status}</span></td><td>{money(o.total)}</td></tr>)}</tbody>
            </table>
          </section>
        )}

        <ChatPanel entityType="project" entityId={projectId} />
      </div>
    </Shell>
  );
}

/* ───────────────────────── chat panel ───────────────────────── */
function ChatPanel({ entityType, entityId }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const { user } = useAuth();
  const load = useCallback(() => {
    axios.get(`${API}/chat/${entityType}/${entityId}/messages`).then(({ data }) => setMessages(data));
  }, [entityType, entityId]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return;
    const { data } = await axios.post(`${API}/chat/${entityType}/${entityId}/messages`, { body });
    setMessages((m) => [...m, data]); setBody("");
  };
  return (
    <section className="st-card st-span3" data-testid="chat-panel">
      <h2 className="st-card-t">Project chat <span className="st-muted">({messages.length})</span></h2>
      <div className="st-chat" data-testid="chat-messages">
        {messages.length === 0 ? <div className="st-empty">No messages yet. Start the conversation.</div> :
          messages.map((m) => (
            <div key={m.id} className={`st-msg ${m.author_id === user?.id ? "mine" : ""}`}>
              <div className="st-msg-h">{m.author_name} · {m.author_role}</div>
              <div className="st-msg-b">{m.body}</div>
            </div>
          ))}
      </div>
      <form className="st-chat-form" onSubmit={send}>
        <input data-testid="chat-input" placeholder="Write a message…" value={body} onChange={(e) => setBody(e.target.value)} />
        <button className="st-btn sm" data-testid="chat-send">Send</button>
      </form>
    </section>
  );
}

/* ───────────────────────── report ───────────────────────── */
function ReportPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  useEffect(() => {
    axios.get(`${API}/projects/${projectId}`).then(({ data }) => {
      setProject(data);
      axios.get(`${API}/clients`).then((r) => setClient(r.data.find((c) => c.id === data.client_id) || null));
    });
  }, [projectId]);
  if (!project) return <Shell title="Report"><div className="st-empty">Loading…</div></Shell>;
  const cab = project.design?.cabinets || [];
  const q = project.quote;
  const meta = project.design?.meta || {};
  return (
    <Shell title={`Report · ${project.name}`}>
      <div className="st-report-actions">
        <Link to={`/store/projects/${projectId}`} className="st-back">← Project</Link>
        <button className="st-btn sm" data-testid="print-report" onClick={() => window.print()}>Print / PDF</button>
      </div>
      <div className="st-report" data-testid="report-doc">
        <div className="st-report-head">
          <h2>Kitchen specification &amp; quote</h2>
          <div className="st-report-meta">
            <div><span>Client</span><strong>{client?.name || "—"}</strong></div>
            <div><span>Project</span><strong>{project.name}</strong></div>
            <div><span>Price group</span><strong>{meta.price_group || "G1"}</strong></div>
            <div><span>Door system</span><strong>{meta.door_system || "handle"}</strong></div>
          </div>
        </div>
        <h3 className="st-report-h">Modules ({cab.length})</h3>
        <table className="st-table sm">
          <thead><tr><th>SKU</th><th>Type</th><th>W×D×H</th><th>Price</th></tr></thead>
          <tbody>{cab.map((c) => <tr key={c.id}><td>{c.sku}</td><td>{c.type}</td><td>{c.width_mm}×{c.depth_mm}×{c.height_mm}</td><td>{money(c.price)}</td></tr>)}</tbody>
        </table>
        <h3 className="st-report-h">Pricing</h3>
        {!q ? <div className="st-empty">No quote generated yet.</div> : (
          <table className="st-table sm">
            <tbody>
              {(q.lines || []).map((l) => <tr key={l.code}><td>{l.label}</td><td style={{ textAlign: "right" }}>{money(l.total)}</td></tr>)}
              <tr><td><strong>Subtotal</strong></td><td style={{ textAlign: "right" }}><strong>{money(q.subtotal)}</strong></td></tr>
              <tr><td><strong>Total</strong></td><td style={{ textAlign: "right" }}><strong>{money(q.total)}</strong></td></tr>
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}

/* Store landing = the clients dashboard. Sales is a separate PLATFORM
 * workspace (/sales) — stores have no sales page (mandate 2026-07-05). */
function StoreIndex() {
  return <ClientsPage />;
}

export default function StoreApp() {
  return (
    // bell={false}: the notification bell now lives in the LhcHeader top bar
    // (real, live). The old floating corner bell is retired to avoid duplicates.
    <RealtimeProvider bell={false}>
    <Routes>
      <Route index element={<StoreIndex />} />
      <Route path="clients" element={<ClientsPage />} />
      <Route path="clients/:clientId" element={<ClientDetailPage />} />
      <Route path="projects/:projectId" element={<ProjectDetailPage />} />
      <Route path="projects/:projectId/report" element={<ReportPage />} />
      <Route path="account" element={<AccountPage />} />
      <Route path="*" element={<Navigate to="/store/clients" replace />} />
    </Routes>
    </RealtimeProvider>
  );
}
